/* eslint-disable jsx-a11y/aria-role */
/*
 * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
 * https://github.com/dequelabs/axe-core/milestone/19
 * https://github.com/dequelabs/axe-core/issues/1597
 */
/* eslint-disable no-nested-ternary */
import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import { BLOCKS } from '@contentful/rich-text-types';

import RichText, { fragment } from '../../../Core/RichText';
import Link from '../../../Core/Link';
import { StyledSticky } from '../styles';
import { BannerContext } from '../../../Providers/Banner';
import IndividualBlade from '../../IndividualBlades';
import { PointValue } from '../ColumnPoints/styles';

import {
  Card,
  ContentWrapper,
  ImageWrapper,
  Heading,
  CardImage,
  HeaderImage,
  Body,
  StyledLink,
  Text,
} from './styles';

const ColumnDefault = ({
  dataItem = {},
  componentStyle = '',
  className = '',
  columnCount = '',
  isHeader = false,
  isSticky = false,
  headers = '',
  setActiveColumn = null,
  isNotActive = '',
  index = 0,
}) => {
  // * Null check for dataItem

  if (!dataItem) return null;

  const intl = useIntl();

  // * Destructure dataItem values

  const {
    id,
    selectable,
    colspan,
    heading,
    bodyCopy,
    imageDam,
    link,
    pointValue,
    valueSubtext,
    backgroundStyle,
    ctas,
  } = dataItem;

  const body = {
    renderNode: {
      /*
       * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
       * https://github.com/dequelabs/axe-core/milestone/19
       * https://github.com/dequelabs/axe-core/issues/1597
       */
      [BLOCKS.PARAGRAPH]: (_, children) => <Text role="text">{children}</Text>,
    },
  };

  /**
   * Set scope of <th> elements depending on chart orientation, 3 variations are needed
   * #1 Column scoped chart where the <th>'s at the top are scoped vertically to the cells below
   * #2 Row scoped chart where the <th>'s on the left are scoped horizontally to the cells on the right
   * #3 A dual scoped chart, where the <th>'s at the top are scoped vertically as `col`, however the <th> in the first cell in the first row is scoped as `row`. In addition, the first cells in each additional row are scoped as `row`. */
  let scope = null;
  if (className !== 'column-layout-chart') {
    if (index !== 0 && isHeader) {
      scope = 'col';
    } else if (index === 0) {
      scope = 'row';
    } else {
      scope = null;
    }
  } else if (className === 'column-layout-chart') {
    if (isHeader) {
      scope = 'col';
    } else {
      scope = null;
    }
  } else {
    scope = null;
  }

  let mediaQuery;
  const [mobile, setMobile] = useState(false);
  const matchMedia = (breakpoint = mediaQuery) => {
    if (breakpoint.matches) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      mediaQuery = window.matchMedia(
        '(min-device-width: 0px) and (max-device-width: 667px) and (orientation: landscape), (min-device-width: 0px) and (max-device-width: 374px) and (orientation: portrait), screen only and (min-width: 576px) and (orientation: landscape)',
      );
      mediaQuery.addListener(matchMedia);
      matchMedia();
    }
    return () => {
      mediaQuery.removeListener(matchMedia);
    };
  }, []);

  const {
    state: { notificationBannerState },
  } = useContext(BannerContext);

  let isSelectable = '';

  if (selectable === true) {
    isSelectable = selectable ? 'is-selectable' : '';
  }

  return (
    <Card
      key={id}
      id={imageDam ? imageDam.altText.toLowerCase().replace(/\s/g, '-') : id}
      className={`
        ${className} 
        ${componentStyle} 
        ${backgroundStyle} 
        ${isNotActive} 
        ${isSelectable}
        `}
      columnCount={columnCount}
      onClick={
        className.includes('selectable-card chart') && selectable
          ? () => setActiveColumn(index)
          : null
      }
      as={
        className !== 'column-layout-chart'
          ? index === 0 || isHeader
            ? 'th'
            : null
          : className === 'column-layout-chart' && isHeader
            ? 'th'
            : null
      }
      scope={scope}
      headers={
        className !== 'row-layout-chart' &&
        columnCount === '2' &&
        !isHeader &&
        index !== 0
          ? headers
          : null
      }
      colSpan={colspan}
      role="cell"
      selectable={selectable}
    >
      {isSticky && isHeader ? (
        <StyledSticky
          boundaryElement=".chartArea"
          topOffset={70}
          // Track whether the notification banner status is fixed or not, to justify for padding
          stickyStyle={{
            paddingTop: notificationBannerState === 'fixed' ? '70px' : '0',
            zIndex: '100',
          }}
          lang={intl.locale}
          stickyClassName={mobile ? 'sticky mobile-landscape' : 'sticky'}
        >
          <ContentWrapper
            className={`${className} ${backgroundStyle}`}
            columnCount={columnCount}
          >
            {link ? (
              <>
                <Link className="unstyled-link" id={id} to={link}>
                  {imageDam && (
                    <ImageWrapper>
                      <CardImage src={imageDam} alt="" />
                    </ImageWrapper>
                  )}
                  {heading && (
                    <Heading lang={intl.locale} role="text">
                      <RichText document={heading} />
                    </Heading>
                  )}
                </Link>
                {bodyCopy && (
                  <Body>
                    <RichText document={bodyCopy} options={body} />
                  </Body>
                )}
              </>
            ) : (
              <>
                {imageDam && (
                  <ImageWrapper>
                    <CardImage src={imageDam} alt="" />
                  </ImageWrapper>
                )}
                {heading && (
                  <Heading role="text">
                    <RichText document={heading} />
                  </Heading>
                )}
                {bodyCopy && (
                  <Body>
                    <RichText document={bodyCopy} options={body} />
                  </Body>
                )}
              </>
            )}
          </ContentWrapper>
        </StyledSticky>
      ) : pointValue ? (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <Body
            className={componentStyle}
            /*
             * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
             * https://github.com/dequelabs/axe-core/milestone/19
             * https://github.com/dequelabs/axe-core/issues/1597
             */
            // eslint-disable-next-line jsx-a11y/aria-role
            role="text"
          >
            <PointValue
              aria-label={pointValue === '-' ? `${valueSubtext}` : null}
            >
              {pointValue}
            </PointValue>
          </Body>
        </ContentWrapper>
      ) : (
        <ContentWrapper
          className={`${className} ${backgroundStyle}`}
          columnCount={columnCount}
        >
          {link ? (
            <>
              <Link id={id} to={link}>
                {imageDam && (
                  <ImageWrapper>
                    <HeaderImage src={imageDam} alt="" />
                  </ImageWrapper>
                )}
                {heading && (
                  <Heading lang={intl.locale} role="text">
                    <RichText document={heading} />
                  </Heading>
                )}
              </Link>
              {bodyCopy && (
                <Body>
                  <RichText document={bodyCopy} options={body} />
                </Body>
              )}
              {ctas && (
                <>
                  {ctas.map(cta => {
                    return cta.internal.type === 'ContentfulBlade' ? (
                      <IndividualBlade key={cta.id} data={cta} />
                    ) : (
                      <StyledLink key={cta.id} data={cta}>
                        <RichText document={cta.label} options={fragment} />
                      </StyledLink>
                    );
                  })}
                </>
              )}
            </>
          ) : (
            <>
              {imageDam && (
                <ImageWrapper>
                  <HeaderImage src={imageDam} alt="" />
                </ImageWrapper>
              )}
              {heading && (
                <Heading role="text">
                  <RichText document={heading} />
                </Heading>
              )}
              {bodyCopy && (
                <Body>
                  <RichText document={bodyCopy} options={body} />
                </Body>
              )}
              {ctas && (
                <>
                  {ctas.map(cta => {
                    return cta.internal.type === 'ContentfulBlade' ? (
                      <IndividualBlade key={cta.id} data={cta} />
                    ) : (
                      <StyledLink key={cta.id} data={cta}>
                        <RichText document={cta.label} options={fragment} />
                      </StyledLink>
                    );
                  })}
                </>
              )}
            </>
          )}
        </ContentWrapper>
      )}
    </Card>
  );
};

ColumnDefault.propTypes = {
  dataItem: PropTypes.shape(),
  componentStyle: PropTypes.string,
  className: PropTypes.string,
  columnCount: PropTypes.string,
  isHeader: PropTypes.bool,
  isSticky: PropTypes.bool,
  index: PropTypes.number,
  headers: PropTypes.string,
  setActiveColumn: PropTypes.func,
  isNotActive: PropTypes.string,
};

export default ColumnDefault;
